import {
  CorrelationMatrixSettings,
  FileRepoFilters,
  MeasureType,
  Nullable,
  PermissionValueType,
  ToJsonString
} from "..";
import { DigestSettingsForm } from "../news";
import { INotifications } from "./notifications";

export interface IUserModule {
  user: IUser;
  token: string;
  broadcast_token: string;
  account: IAccount;
  roles: IRole[];
  audiences: IAudiences;
  bookmarks: IBookmark[];
  managers: UserLight[];
  selectedBookmark: Nullable<IBookmark["id"]>;
  opportunity_id: number;
  default_bookmarks: IDefaultAccountBookmarks;

  hideCompletionPopup?: boolean;
  hideExtractorGuide?: boolean;
  hideWelcomePopup?: boolean;
  onboardingCompleted?: boolean;
  gptPrompt?: { role: "system" | "user" | "assistant"; content: string }[];
  showUnlockFullAccess: boolean;
  dealsTableColumns: string[];
  numberFormat: {
    abbreviated: boolean;
    decimal: number;
  };
  filesFilters: FileRepoFilters;
  fileRepoColumns: Record<string, boolean>;
  notifications: INotifications;
  workspaceColumns: Record<string, boolean>;
  portfolioConstruct: number | null;
  amortizationTableColumns: Record<string, boolean>;
  digestSettings: DigestSettingsForm;
}

export interface IAccount {
  id: number;
  name: string;
  domain: string;
  api_domain: string;
  under_maintenance: number;
  logo_url: string;
  is_report_enabled: boolean;
  allow_dataroom: boolean;
  allow_import_deal_from_excel: boolean;
  allow_social: boolean;
  show_benchmark: boolean;
  show_plaid: boolean;
  report_url: string | null;
  azure_sso_enabled: boolean;
  external_report: boolean;
  account_date_format: string | null;
  show_batch_id: boolean;
  allow_news: boolean;
  allow_account_invitation: boolean;
  allow_fees_calc: boolean;
  allow_deal_tab_open: boolean;
  allow_gpt: boolean;
  allow_effective_range: boolean;
  show_telescope: boolean;
  enable_crypto: boolean;
  html_template: string | null;
  allow_IBI_reports: boolean;
  hedge_fund: boolean;
  adjustable_columns: boolean;
  nullify_unfunded_commitment: boolean;
  batch_runtime_summary: boolean;
  show_meitav_management_fees: boolean;
  show_hcs_view: boolean;
  enable_user_management: boolean;
  look_through_investments: boolean;
  disable_apply_filters: boolean;
  allow_use_date_range_switch: boolean;
  show_cashflow: boolean;
  show_debt: boolean;
  show_secondary_pricing: boolean;
  single_asset_report: boolean;
  show_correlations: boolean;
  support_investor_groups: boolean;
  allow_split_by_group: boolean;
  investor_level_characteristics: boolean;
  allow_ai_extraction: boolean;
  show_global_investor_filter: boolean;
  is_freemium: boolean;
  upgraded_from_freemium: boolean;
  freemium_account_settings: {
    max_datarooms_connections: number;
    max_deals: number;
    number_of_datarooms_connections: number;
    number_of_deals: number;
    max_files: number;
    number_of_files: number;
  } | null;
  validation_panel: boolean;
  dataroom_phone_number: string | null;
  sanitize_documents: boolean;
  allow_extended_groups: boolean;
  show_smartsheet: boolean;
  show_ai_report_generator: boolean;
  show_ai_intelligence: boolean;
  enable_portfolio_construction: boolean;
  show_localization: boolean;
  welcome_video_url: string | null;
  allow_ai_deal_creation: boolean;
  show_recommendations: boolean;
}

export interface UserLight {
  id: number;
  profile_photo_url: string;
  full_name: string;
}

export interface IUser extends UserLight {
  first_name: string;
  last_name: string;
  company_name: string;
  account_id: number;
  phone_number: string;
  email: string;
  email_verified_at: string;
  profile_photo_path: string;
  enable_2fa: boolean;
  email_notification: boolean;
  role: IRole;
  role_id: number;
  blank_template_id: number;
  created_at: string;
  password_reminder: boolean;
  password_expired: boolean;
  has_only_external_accounts: boolean;
  permissions?: Record<string, PermissionValueType>;
  has_shared_global_documents: boolean | null;
  has_shared_deals: boolean | null;
  last_seen: string | null;
}
export interface IRole {
  id: number;
  name: IRoleName;
  presentation_name: string;
  short_name: string;
  deleted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface IAudiences {
  everyone: string;
  tenants_team: string;
  capital_seeker: string;
  guest: string;
  viewer: string;
  me: string;
}

export type KeyOfAudiences = keyof IAudiences;

export type IRoleName = "team-member" | "guest" | "admin" | "viewer" | "team-viewer" | "investor";

export type IBookmarkType =
  | "report"
  | "deal"
  | "quick_view"
  | "investor_view"
  | "correlation_matrix"
  | "data_operations";
export interface IBookmarkUser {
  id: number;
  first_name: string;
  last_name: string;
  profile_photo_path: string | null;
  account_id: number;
  profile_photo_url: string;
  full_name: string;
}

export enum IReportType {
  PORTFOLIO = "portfolio_analysis",
  COMPARISON = "comparison",
  DEAL_VIEW = "deal_view",
  EXTERNAL = "external",
  PORTFOLIO_OVERVIEW = "portfolio_overview"
}

export interface IBookmark {
  id: number;
  name: string;
  params: string;
  account_id: number;
  user_id: number;
  type: IBookmarkType;
  is_default: boolean;
  is_private: boolean;
  report_type: Nullable<IReportType.PORTFOLIO | IReportType.COMPARISON | IReportType.DEAL_VIEW | IReportType.EXTERNAL>;
  user: IBookmarkUser;
  account_default: boolean;
}

export interface IDefaultAccountBookmarks {
  deal: string | null;
  quick_view: string | null;
  report: { report_type: string; params: string | null }[];
  investor_view: string | null;
  correlation_matrix: ToJsonString<CorrelationMatrixSettings> | null;
  data_operations: string | null;
}

export interface ISendQuestionData {
  message: string;
  type: "advice" | "co-invest" | "sell" | "question" | "connect";
  page: string;
  category: string;
  files?: Array<File>;
}

export interface AccountForSwitch {
  id: number;
  logo: string;
  name: string;
  domain: `${string}.${string}.${string}`;
  api_domain: `${string}.api.${string}.${string}`;
  notifications: number;
}

export interface AccountSettings {
  lock_day: number | null;
  lock_frequency: "monthly" | "quarterly" | "yearly";
  debt_reminder_days: number | null;
  debt_reminder_users: number[] | null;
  validation_settings: {
    rules: Array<{
      measure_type: MeasureType | null;
      key: string | null;
      threshold: string | null;
      absolute_value: number | null;
      relative_value: number | null;
    }>;
    message: string | null;
  };
}

export * from "./notifications";
export * from "./onboarding";
export * from "./social";
export * from "./dashboard";
